import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from 'src/components/Layout';
import Navbar from 'src/components/NavBar';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import SelectCompany from 'src/components/SelectCompany';
import Button from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import PopUp from 'src/components/design-system/PopUp';
import useCompany from 'src/hooks/useCompany';
import useGuideMe from 'src/hooks/useGuideMe';
import usePeriodView from 'src/hooks/usePeriodView';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import { initialPeriodCheckbookDataState } from 'src/sections/view_checkbook/ViewCheckbookContext';
import { Company } from 'src/types/Companies';
import useGuideMeSteps from './GuideMeSteps';
import ImportExport from './ImportExportButton';
import ViewAllAttributeUserAssignments from './ViewAllAttributeUserAssignments';
import ViewCheckbookPeriodWarningTable from './ViewCheckbookPeriodWarningTable';
import ViewCheckbookTransactionLog from './ViewCheckbookTransactionLog';

const ViewCheckbookNavbar = () => {
  const { t } = useTranslation();
  const {
    isEditMode,
    setIsEditMode,
    periodWarningList,
    hasPeriodErrors,
    hasPeriodWarnings,
    selectedCompany,
    setPeriodCheckbookDataState,
    setSelectedPeriodDepartment,
    departmentList,
    confirmBeforeProceeding,
    isPeriodTableDirty,
    pageTitle,
    setEditingGlCodePeriodBudget,
    setOriginalGlCodePeriodBudget,
    setDropDownPeriodValue,
    handleSaveClick,
    glCodeWarningsColumnData,
    modalStepCount,
    isModalOpen,
    handleCloseModal,
    isCheckbookEditable,
    isSaveGlCodeBudgetLoading,
    uniquePeriodErrorList,
  } = useViewCheckbook();

  const { periodViewData } = usePeriodView();
  const { changeCompany } = useCompany();
  const guideMeSteps = useGuideMeSteps();
  const { guideMe } = useGuideMe({
    steps: guideMeSteps,
    guideId: 'view-budget',
  });

  const handleCompanyChange = useCallback(
    (company: Company) => {
      confirmBeforeProceeding(() => {
        setDropDownPeriodValue(null);
        changeCompany(company);
        setIsEditMode(false);
        setSelectedPeriodDepartment(departmentList[0]);
        setPeriodCheckbookDataState(initialPeriodCheckbookDataState);
      });
    },
    [
      changeCompany,
      confirmBeforeProceeding,
      departmentList,
      setDropDownPeriodValue,
      setIsEditMode,
      setPeriodCheckbookDataState,
      setSelectedPeriodDepartment,
    ]
  );

  const handleEditClick = useCallback(() => {
    setIsEditMode(true);
    setEditingGlCodePeriodBudget(periodViewData);
    setOriginalGlCodePeriodBudget(periodViewData);
  }, [periodViewData, setEditingGlCodePeriodBudget, setIsEditMode, setOriginalGlCodePeriodBudget]);

  const handleCancelClick = useCallback(async () => {
    setOriginalGlCodePeriodBudget([]);
    setEditingGlCodePeriodBudget([]);
    setIsEditMode(false);
    setPeriodCheckbookDataState((prev) => ({ ...prev, sort: [{ field: 'code', dir: 'asc' }] }));
  }, [
    setEditingGlCodePeriodBudget,
    setIsEditMode,
    setOriginalGlCodePeriodBudget,
    setPeriodCheckbookDataState,
  ]);

  const modalSteps = useMemo(
    () => [
      {
        title: 'Warning',
        content: (
          <>
            <span className="text-sm ">
              The code(s) below could not be activated because they do not meet the required GL Code
              format(s). All other changes were saved successfully.
            </span>

            <ViewCheckbookPeriodWarningTable glCodeWarningsColumnData={glCodeWarningsColumnData} />
          </>
        ),
        isConfirmationModal: true,
      },
    ],
    [glCodeWarningsColumnData]
  );

  const handleConfirm = useCallback(() => {
    handleCloseModal();
  }, [handleCloseModal]);

  return (
    <>
      <Navbar title={pageTitle} />
      <Layout>
        <nav className="flex justify-between items-center flex-wrap min-h-[56px]">
          <div className="w-[400px]">
            <SelectCompany
              className="items-center"
              onChange={handleCompanyChange}
              isControlled
              selectSuperAdmin
            />
          </div>

          <div className="flex flex-grow items-center justify-center flex-row ">
            {!!selectedCompany?.companyId && (
              <>
                {isCheckbookEditable ? (
                  isEditMode ? (
                    <div className="flex gap-1">
                      <PopUp
                        popupAlign={{ horizontal: 'center', vertical: 'top' }}
                        content={
                          <div className="m-1 max-w-[350px]">
                            <PopUpContentWrapper
                              type="Error"
                              contents={[...uniquePeriodErrorList]}
                            />
                            <PopUpContentWrapper type="Warning" contents={[...periodWarningList]} />
                          </div>
                        }
                        className="max-h-[200px]"
                        show={hasPeriodErrors || hasPeriodWarnings}
                      >
                        <Button
                          onClick={handleSaveClick}
                          disabled={hasPeriodErrors || !isPeriodTableDirty}
                          loading={isSaveGlCodeBudgetLoading}
                          color="primary"
                        >
                          {t('Save')}
                        </Button>
                      </PopUp>
                      <Button color="error" onClick={handleCancelClick}>
                        {t('Cancel')}
                      </Button>
                    </div>
                  ) : (
                    <Button color="primary" onClick={handleEditClick}>
                      {t('Edit')}
                    </Button>
                  )
                ) : (
                  <></>
                )}
              </>
            )}
          </div>
          <div className="flex flex-row flex-wrap justify-end">
            {!!selectedCompany?.companyId && (
              <>
                <ViewAllAttributeUserAssignments />
                <ViewCheckbookTransactionLog />
                <ImportExport />
                {guideMe}
              </>
            )}
          </div>
        </nav>
      </Layout>
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        title={modalSteps?.[modalStepCount]?.title}
        id="modal"
        width="700px"
        className="max-h-[90vh]"
      >
        {modalSteps?.[modalStepCount]?.content}
        <DialogActionsBar>
          <div className="flex justify-end mr-1">
            <Button
              color="info"
              size="md"
              onClick={handleConfirm}
              className={modalSteps?.[modalStepCount]?.isConfirmationModal ? 'ml-10' : ''}
            >
              {modalSteps?.[modalStepCount]?.isConfirmationModal ? 'OK' : 'Cancel'}
            </Button>
          </div>
        </DialogActionsBar>
      </Dialog>
    </>
  );
};

export default ViewCheckbookNavbar;
